import React, { useState } from 'react';
import { IFilterPanelProps, IReport } from './types';
import DateFnsUtils from '@date-io/date-fns';
import { useHistory } from 'react-router-dom';
import { IVenue } from 'app/reducers/venueSlice';
import { RootState } from 'app/main/rootReducer';
import { useDispatch, useSelector } from 'react-redux';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CloudDownload from '@material-ui/icons/CloudDownload';
import { makeStyles, Button, TextField, IconButton, Checkbox, Tooltip } from '@material-ui/core';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { GeneratePDF,ReportingOptions, DashboardPage,
   GetDashboardStatistics, DashboardDivIdList } from './helper';
import { saveFromDate, saveMultipleSelectedVenues, 
  saveSelectedReport, saveSelectedVenue, saveToDate, 
  saveToDateDisplay, setBookingServiceGroupPaxDate,setExportBtnClicked } from 'app/reducers/dashboardSlice';
import { formatDate } from './service';
import { CheckBox, CheckBoxOutlineBlank } from '@material-ui/icons';

const NS = 'FilterPanel';

/** Styles for filter panel */
const useStyles = makeStyles({
  root: {
    '& .MuiOutlinedInput-adornedEnd': {
      paddingRight: '0px'
    },
    '& .MuiFormControl-root': {
      width: '230px'
    },
    width: '1080px',
    display: 'flex',
    alignItems: 'center',
    marginBottom: '20px',
    justifyContent: 'space-between'
  }
});

/**
 * Renders the panel to filter the data
 */
export const FilterPanel = (props: IFilterPanelProps) => {
  const [fromError, setFromError] = useState(null);
  const [toError, setToError] = useState(null);

  // Custom From error msg
  const fromErrorMessage = React.useMemo(() => {
    switch (fromError) {
      case 'Date should not be after maximal date':{
        return 'Date must be within a week of the To Date';
      }

      default: {
        return fromError;
      }
    }
  }, [fromError]);

  // Custom To error msg
  const toErrorMessage = React.useMemo(() => {
    switch (toError) {
      case 'Date should not be after maximal date':{
        return 'Date must be within a week of the From Date';
      }

      default: {
        return toError;
      }
    }
  }, [toError]);

  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const { venues, filteredBookingList } = useSelector((state: RootState) => state.venueReducer);
  const { filterParameters, avgPerPaxSpendStats, avgPerSpendDurationStats } = useSelector((state: RootState) => state.dashboardReducer);
  const { fromDate, toDateDisplay, selectedReport, selectedVenue, selectedVenues } = filterParameters;

  const IsFromDateAndToDateValid = (fromDate: Date, toDate: Date) => {
    const differenceInTime = toDate.getTime() - fromDate.getTime();
    const differenceInDays = differenceInTime / (1000 * 3600 * 24);

    if (differenceInDays < 0) {
      setFromError('Date must be within a week of the To Date')
    } else if (differenceInDays > 7) {
      setToError('Date must be within a week of the From Date')
    } else {
      setFromError(null);
      setToError(null);
    }
  }

  const setFromDateAndDispatch = (selectedDate: Date) => {
    const selectedDateDisplay = formatDate(selectedDate);

    dispatch(saveFromDate(selectedDateDisplay));
    IsFromDateAndToDateValid(selectedDate, new Date(toDateDisplay));
  }

  const setToDateAndDispatch = (selectedDate: Date) => {
    
    IsFromDateAndToDateValid(new Date(fromDate), selectedDate);

    const selectedDateDisplay = formatDate(selectedDate);
    selectedDate.setDate(selectedDate.getDate() + 1);
    dispatch(saveToDateDisplay(selectedDateDisplay));
    dispatch(saveToDate(formatDate(selectedDate)));
  }

  /**
   * On selecting the report, navigate to the selected page by updating the url e.g.
   * "dashboard/report", open <ReportingDetailsPage />
   * "dashboard/amount", open <AmountSpentDetailsPage />
   * "dashboard/guests", open <BookingDetailsPage />
   * "dashboard/services", open <BookingsCountByServiceWrapper />
   */
  const navigateToSelectedPage = (page: IReport) => {

    // Update the store
    dispatch(saveSelectedReport(page));

    const {pathname} = history.location;
    // When on home page i.e. "/", append the "dashboard/" text inorder to update the url.
    const path = pathname === "/" ? `dashboard/${page.urlParamText}` : page.urlParamText;
    history.push(path);
  }

  /**
   * Display download pdf button on reporting details page when data is available.
   */
  const shouldShowDownloadButton = () => {

      if (selectedReport.id === DashboardPage.REPORTING_DETAILS) {
          if ((filteredBookingList?.length ?? 0) > 0) return true;
          if ((avgPerPaxSpendStats?.length ?? 0) > 0) return true;
          if ((avgPerSpendDurationStats?.length ?? 0) > 0) return true;
      }
      else if (selectedReport.id === DashboardPage.BOOKING_DETAILS) {
          if ((filteredBookingList?.length ?? 0) > 0) return true;
          return false;
      }
    return false;
  }

  const icon = <CheckBoxOutlineBlank fontSize="small" />;
  const checkedIcon = <CheckBox fontSize="small" />;
  
  if (selectedVenue.id === 0 || venues.length === 0) return null;

  const allSelected = selectedVenues.length === venues.length;

  const getValues = (): IVenue[] => {
    return [...(venues.filter(f => selectedVenues.findIndex(i => i.id === f.id) >= 0))] as IVenue[];
  }

  const handleLoad = async () => {
    props.onHandleLoadSpinner(true);
    await GetDashboardStatistics(selectedReport, filterParameters);
    props.onHandleLoadSpinner(false);
  }

  return (
    <div className={classes.root}>
      <Autocomplete
        autoComplete={true}
        disableClearable={true}
        value={selectedReport}
        options={ReportingOptions}
        getOptionLabel={report => report.name}
        renderInput={(params) => <TextField {...params} label="Reports" variant="outlined" />}
        onChange={(event: React.ChangeEvent<{}>, value: IReport) => navigateToSelectedPage(value)}
      />
      {selectedReport.id === DashboardPage.SERVICES && 
        <Autocomplete
          multiple
          options={venues}
          id="multiselect-venues"
          value={getValues()}
          getOptionLabel={(venue: IVenue) => venue.name}
          renderOption={(option, { selected }) => {
            const allSelectedPropOption = allSelected ? {checked: true} : {}
            return (<li>
              <Checkbox 
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected}
                {...allSelectedPropOption}
              />
              {option.name}
            </li>);
          }}
          defaultValue={[venues.find(f => f.id === selectedVenue.id)]}
          renderInput={(params) => <TextField {...params} label="venues" style={{ minWidth: "400px" }} variant="outlined" />}
          renderTags={(values) => {
            const result = values.map(value => value.name).join(', ');
            const suffix = (result.length <= 35 ) ? '' : '...';
            return `${result.substring(0, (result.length <= 35 ? result.length: 35))}${suffix}`;
          }}
          onChange={(event: React.ChangeEvent<{}>, values: IVenue[]) => {
              dispatch(saveMultipleSelectedVenues(values));
          }}
        />
      }
      {selectedReport.id !== DashboardPage.SERVICES && 
        <Autocomplete
        options={venues}
        id="single-select-venue"
        autoComplete
        value={selectedVenue}
        disableClearable={true}
        getOptionLabel={venue => venue.name}
        renderInput={(params) => <TextField {...params} label="Venue" variant="outlined" />}
        onChange={(event: React.ChangeEvent<{}>, value: IVenue) => dispatch(saveSelectedVenue(value))}
      />
      }
      {selectedReport.id === DashboardPage.BOOKING_DETAILS &&
        <>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <KeyboardDatePicker
              autoOk
              variant="inline"
              inputVariant="outlined"
              label="From Date"
              format="dd/MM/yyyy"
              value={fromDate}
              onChange={setFromDateAndDispatch}
              error={fromError?.length > 0}
              helperText={fromErrorMessage}
            />
          </MuiPickersUtilsProvider>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <KeyboardDatePicker
              autoOk
              variant="inline"
              inputVariant="outlined"
              label="To Date"
              format="dd/MM/yyyy"
              value={toDateDisplay}
              maxDate={new Date(fromDate).setDate(new Date(fromDate).getDate() + 7)}
              onChange={setToDateAndDispatch}
              onError={(newError) => setToError(newError)}
              helperText={toErrorMessage}
            />
          </MuiPickersUtilsProvider>
        </>
      }
      {selectedReport.id == DashboardPage.SERVICES &&
       <>
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <KeyboardDatePicker
              autoOk
              variant="inline"
              inputVariant="outlined"
              label="Date"
              format="dd/MM/yyyy"
              value={filterParameters.serviceGroupingPaxCountDate}
              onChange={date => dispatch(setBookingServiceGroupPaxDate(formatDate(date)))}
            />
          </MuiPickersUtilsProvider>
       </>
      }
      {selectedReport.id !== DashboardPage.BOOKING_DETAILS 
        && selectedReport.id !== DashboardPage.SERVICES &&
        <>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <KeyboardDatePicker
              autoOk
              variant="inline"
              inputVariant="outlined"
              label="From Date"
              format="dd/MM/yyyy"
              value={fromDate}
              onChange={date => dispatch(saveFromDate(formatDate(date)))}
            />
          </MuiPickersUtilsProvider>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <KeyboardDatePicker
              autoOk
              variant="inline"
              inputVariant="outlined"
              label="To Date"
              format="dd/MM/yyyy"
              value={toDateDisplay}
              onChange={setToDateAndDispatch}
            />
          </MuiPickersUtilsProvider>
          </>
      }
      <Button
        variant="contained"
        color="primary"
        disabled={fromError?.length > 0 || toError?.length > 0}
        onClick={handleLoad}
      >Load</Button>
      {
              (() => { 
                  if (shouldShowDownloadButton()) {
                      if (selectedReport.id === DashboardPage.REPORTING_DETAILS) {
                          return <IconButton aria-label="Download" onClick={() => GeneratePDF("dashboard", DashboardDivIdList)  }>
                              <CloudDownload />
                          </IconButton>
                      }
                      else if (selectedReport.id === DashboardPage.BOOKING_DETAILS) {
                          return <Tooltip title="Download CSV" arrow><IconButton aria-label="DownloadCsv" onClick={() => dispatch(setExportBtnClicked(true))}>
                              <CloudDownload />
                          </IconButton></Tooltip>;
                      } 
                  }
                  return (null);
              })()
      }
    </div>
  )
};